import { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Calendar, Move, CalendarClock, GripVertical, Pencil, Trash2, Eye, EyeOff, Link2 } from 'lucide-react';
import { toast } from 'sonner';
import { LANE_COLORS, DEFAULT_LANE_COLOR, TIME_SLOTS, formatTime, timeStringToMinutes, getDurationColor } from './utils';

/**
 * Renders the body rows of the calendar table. Extracted as its own
 * component so the tbody's only child is a JSX element (not an inline
 * `{visibleSlots.map(...)}` expression) — this prevents the dev-tooling
 * `<span style="display:contents">` wrapper from being injected inside
 * `<tbody>`, which React warns about as invalid HTML nesting.
 */
const CalendarRows = ({
  visibleSlots,
  lanes,
  draggedBooking,
  isDragging,
  dropTargetSlot,
  getSlotStatus,
  getSlotSpanInfo,
  onSlotClick,
  onBookedSlotClick,
  onConflictClick,
  onEditBlock,
  onDeleteBlock,
  onDragStart,
  onDragEnd,
  onDragOver,
  onDragLeave,
  onDrop,
}) => (
  <>
    {visibleSlots.map((timeSlot) => (
      <tr key={timeSlot} className="border-b border-slate-100 hover:bg-slate-50/50">
        <td className="p-2 text-xs text-slate-500 font-medium sticky left-0 bg-white">
          {formatTime(timeSlot)}
        </td>
        {lanes.map((lane) => {
          const status = getSlotStatus(lane.id, timeSlot);
          const spanInfo = getSlotSpanInfo(lane.id, timeSlot);
          const isDropTarget = dropTargetSlot?.laneId === lane.id && dropTargetSlot?.timeSlot === timeSlot;

          if (!spanInfo.isStart && (spanInfo.type === 'booked' || spanInfo.type === 'blocked')) {
            return <td key={`${lane.id}-${timeSlot}`} className="p-0 border-l border-slate-100" />;
          }
          const slotSpan = (spanInfo.isStart && spanInfo.span > 1) ? spanInfo.span : 1;
          const laneColor = LANE_COLORS[lane.id] || DEFAULT_LANE_COLOR;

          return (
            <td
              key={`${lane.id}-${timeSlot}`}
              className={`p-1 border-l border-slate-100 ${slotSpan > 1 ? 'relative overflow-visible' : ''}`}
              onClick={() => (status.type === 'available' || status.type === 'closed') && !isDragging && onSlotClick(lane.id, timeSlot)}
            >
              {status.type === 'available' && (
                <div
                  className={`h-12 rounded transition-all ${isDropTarget
                    ? 'bg-blue-100 border-2 border-dashed border-blue-400'
                    : isDragging
                      ? 'bg-green-50 border-2 border-dashed border-green-400 cursor-copy'
                      : `cursor-pointer hover:bg-green-100 hover:border hover:border-green-300 ${laneColor.light}`}`}
                  title={isDragging ? 'Drop here to reschedule' : 'Click to book'}
                  onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); onDragOver(e, lane.id, timeSlot, true); }}
                  onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); }}
                  onDragLeave={onDragLeave}
                  onDrop={(e) => { e.preventDefault(); e.stopPropagation(); onDrop(e, lane.id, timeSlot); }}
                  data-drop-target="true" data-lane-id={lane.id} data-time-slot={timeSlot}
                >
                  <span className={`text-xs p-1 block pointer-events-none select-none ${isDragging ? 'text-green-600 font-medium' : 'text-slate-400'}`}>
                    {isDragging ? '↓ Drop Here ↓' : 'Available'}
                  </span>
                </div>
              )}
              {status.type === 'closed' && (
                <div
                  className={`h-12 rounded transition-all group ${isDropTarget
                    ? 'bg-blue-100 border-2 border-dashed border-blue-400'
                    : isDragging
                      ? 'bg-amber-50 border-2 border-dashed border-amber-400 cursor-copy'
                      : 'bg-slate-100 border border-slate-200 cursor-pointer hover:bg-amber-50 hover:border-amber-300'}`}
                  title={isDragging ? 'Drop here to reschedule (outside hours)' : 'Outside business hours - Click to override'}
                  onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); onDragOver(e, lane.id, timeSlot, true); }}
                  onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); }}
                  onDragLeave={onDragLeave}
                  onDrop={(e) => { e.preventDefault(); e.stopPropagation(); onDrop(e, lane.id, timeSlot); }}
                  data-drop-target="true" data-lane-id={lane.id} data-time-slot={timeSlot}
                >
                  <span className={`text-xs p-1 block pointer-events-none select-none ${isDragging ? 'text-amber-600 font-medium' : 'text-slate-400 group-hover:text-amber-600'}`}>
                    {isDragging ? '↓ Drop Here ↓' : 'Closed'}
                  </span>
                  {!isDragging && (
                    <span className="text-[10px] text-slate-300 p-1 pt-0 block pointer-events-none select-none group-hover:text-amber-500">Click to override</span>
                  )}
                </div>
              )}
              {status.type === 'blocked' && (
                <div
                  className={`${slotSpan > 1 ? 'absolute left-0 right-0 top-0 z-10' : 'h-12'} bg-red-100 border border-red-200 rounded p-1 group flex flex-col justify-center cursor-pointer hover:bg-red-200 transition-colors`}
                  style={slotSpan > 1 ? { height: `calc(${slotSpan * 100}% + ${(slotSpan - 1)}px)` } : undefined}
                  title={`${status.data.reason || 'Coaching'} · Click to edit`}
                  onClick={(e) => { e.stopPropagation(); onEditBlock(status.data); }}
                  data-testid={`calendar-block-${status.data.id}`}
                >
                  <span className="text-xs font-medium text-red-700 truncate block">
                    {status.data.small_group_session_name || status.data.class_name || status.data.reason || 'Blocked'}
                    {status.data.recurrence_id && (
                      <CalendarClock className="inline h-3 w-3 ml-1 text-red-500" aria-label="Recurring block" />
                    )}
                  </span>
                  {status.data.small_group_slot_id && status.data.small_group_max_capacity != null && (
                    <span className="text-[10px] font-semibold text-red-700 bg-red-50 border border-red-200 rounded px-1 py-0 mt-0.5 inline-block w-fit"
                          data-testid={`sg-slot-count-${status.data.id}`}>
                      {status.data.small_group_registered_count || 0} / {status.data.small_group_max_capacity}
                    </span>
                  )}
                  {slotSpan > 1 && (
                    <span className="text-[10px] text-red-500">
                      {status.data.start_time} - {status.data.end_time}
                    </span>
                  )}
                  <div className="absolute top-0 right-0 flex opacity-0 group-hover:opacity-100 bg-red-50 rounded-bl">
                    {status.data.small_group_slot_id && (
                      <Button variant="ghost" size="icon" className="h-5 w-5 text-violet-600 hover:bg-violet-100"
                        onClick={(e) => {
                          e.stopPropagation();
                          const url = `${window.location.origin}/register/small-group/${status.data.small_group_slot_id}`;
                          navigator.clipboard?.writeText(url).catch(() => {});
                          toast.success('Registration link copied');
                        }}
                        title="Copy shareable registration link" data-testid={`reglink-block-${status.data.id}`}>
                        <Link2 className="h-3 w-3" />
                      </Button>
                    )}
                    <Button variant="ghost" size="icon" className="h-5 w-5 text-red-600 hover:bg-red-200"
                      onClick={(e) => { e.stopPropagation(); onEditBlock(status.data); }}
                      title="Edit block" data-testid={`edit-block-${status.data.id}`}>
                      <Pencil className="h-3 w-3" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-5 w-5 text-red-600 hover:bg-red-200"
                      onClick={(e) => { e.stopPropagation(); onDeleteBlock(status.data.id); }}
                      title="Delete block" data-testid={`delete-block-${status.data.id}`}>
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              )}
              {status.type === 'booked' && (() => {
                // iter330b — Always render from `spanInfo.data`, which
                // uses the "prefer booking starting at this slot"
                // primary-selection rule. `status.data` from
                // `getSlotStatus` returns the first overlapping
                // booking and would drop the later card of a
                // staggered overlap (e.g. 17:00-18:00 vs 17:30-18:30
                // at the 17:30 slot).
                const bookingCard = spanInfo.data || status.data;
                const durColor = getDurationColor(bookingCard.duration);
                return (
                <div
                  draggable={true}
                  onDragStart={(e) => onDragStart(e, bookingCard)}
                  onDragEnd={onDragEnd}
                  className={`${slotSpan > 1 ? 'absolute left-0 right-0 top-0 z-10' : 'h-12'} ${durColor.bg} border-2 ${durColor.border} rounded p-1 cursor-grab active:cursor-grabbing hover:ring-2 hover:ring-blue-400 hover:ring-offset-1 transition-all group flex flex-col justify-center ${draggedBooking?.id === bookingCard.id ? 'opacity-50 ring-2 ring-blue-400' : ''}`}
                  style={slotSpan > 1 ? { height: `calc(${slotSpan * 100}% + ${(slotSpan - 1)}px)` } : undefined}
                  title={`${bookingCard.customer_name} - Drag to reschedule or click to view`}
                  onClick={() => !isDragging && onBookedSlotClick(bookingCard)}
                  data-testid={`calendar-booking-${bookingCard.id}`}
                >
                  <div className="flex items-center gap-1">
                    <div className="flex-shrink-0 p-0.5 rounded bg-slate-200/50 group-hover:bg-blue-200 transition-colors">
                      <GripVertical className="h-4 w-4 text-slate-500 group-hover:text-blue-600" />
                    </div>
                    <span className={`text-xs font-medium ${durColor.text} truncate flex-1`}>
                      {bookingCard.customer_name?.split(' ')[0] || 'Booked'}
                    </span>
                    {bookingCard.recurrence_id && (
                      <CalendarClock className="h-3 w-3 text-purple-600 flex-shrink-0" aria-label="Recurring booking" />
                    )}
                    {/* iter329b — Conflict badge is now clickable.
                        Fires `onConflictClick` with the full list of
                        conflicting bookings (primary + others on the
                        same slot) so the parent can open a resolver
                        modal. `stopPropagation` prevents the booking-
                        detail click from firing at the same time. */}
                    {(spanInfo.conflict_count || 0) > 0 && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (onConflictClick) {
                            onConflictClick([bookingCard, ...(spanInfo.conflicts || [])]);
                          }
                        }}
                        className="flex-shrink-0 text-[9px] font-bold text-white bg-red-600 hover:bg-red-700 rounded px-1 py-0 border border-red-800 shadow-sm cursor-pointer"
                        title={`Also booked: ${spanInfo.conflicts.map((c) => `${c.customer_name || 'Unknown'} (${c.start_time}-${c.end_time})`).join(' · ')}. Click to resolve.`}
                        data-testid={`calendar-booking-conflict-${bookingCard.id}`}
                      >
                        ⚠ +{spanInfo.conflict_count}
                      </button>
                    )}
                  </div>
                  {slotSpan > 1 && (
                    <span className={`text-[10px] ${durColor.text} opacity-70 ml-5`}>
                      {bookingCard.start_time} - {bookingCard.end_time}
                    </span>
                  )}
                  <Badge
                    variant="outline"
                    className={`text-[10px] px-1 py-0 ml-5 ${
                      bookingCard.payment_status === 'paid'
                        ? 'bg-green-100 text-green-700'
                        : bookingCard.payment_status === 'processing'
                          ? 'bg-indigo-100 text-indigo-700'
                          : bookingCard.payment_session_id
                            ? 'bg-blue-100 text-blue-700'
                            : 'bg-yellow-100 text-yellow-700'
                    }`}
                  >
                    {bookingCard.payment_status === 'paid'
                      ? '✓ Paid'
                      : bookingCard.payment_status === 'processing'
                        ? '⟳ Processing'
                        : bookingCard.payment_session_id
                          ? '⧖ Pending'
                          : '$ Due'}
                  </Badge>
                </div>
                );
              })()}
            </td>
          );
        })}
      </tr>
    ))}
  </>
);

/**
 * The big time × lane calendar table. Pure presentational — all data and
 * callbacks come from the parent (LaneManagement.js).
 *
 * The grid hides time slots outside business hours by default to keep the
 * view focused on bookable rows. Off-hour slots that already contain a
 * booking or block remain visible so admins never "lose" data — the toggle
 * just controls whether the empty rows above/below the open window appear.
 */
export const CalendarGrid = ({
  lanes,
  bookings,
  blocks,
  availability,
  draggedBooking,
  isDragging,
  dropTargetSlot,
  getSlotStatus,
  getSlotSpanInfo,
  onSlotClick,
  onBookedSlotClick,
  onConflictClick,
  onEditBlock,
  onDeleteBlock,
  onDragStart,
  onDragEnd,
  onDragOver,
  onDragLeave,
  onDrop,
}) => {
  const [showOffHours, setShowOffHours] = useState(false);

  const visibleSlots = useMemo(() => {
    if (showOffHours) return TIME_SLOTS;
    const openTime = availability?.business_hours?.open_time;
    const closeTime = availability?.business_hours?.close_time;
    // If business hours aren't known, fall back to showing all slots so the
    // admin doesn't see an empty grid on misconfigured days.
    if (!openTime || !closeTime) return TIME_SLOTS;
    const openMin = timeStringToMinutes(openTime);
    const closeMin = timeStringToMinutes(closeTime);
    const withinHours = (slot) => {
      const m = timeStringToMinutes(slot);
      return m >= openMin && m < closeMin;
    };
    // Keep any off-hours slot that already has a booking or block so we
    // never visually drop an existing reservation.
    const hasContent = (slot) => lanes.some((lane) => {
      const s = getSlotStatus(lane.id, slot);
      return s.type === 'booked' || s.type === 'blocked';
    });
    return TIME_SLOTS.filter((slot) => withinHours(slot) || hasContent(slot));
  }, [showOffHours, availability, lanes, getSlotStatus]);

  const hiddenCount = TIME_SLOTS.length - visibleSlots.length;

  return (
  <Card className="border border-slate-200 overflow-hidden">
    <CardHeader className="border-b border-slate-100 py-3">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <CardTitle className="font-heading text-lg flex items-center gap-2">
          <Calendar className="h-5 w-5 text-blue-500" />
          Lane Schedule
        </CardTitle>
        <div className="flex items-center gap-3 text-xs flex-wrap">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowOffHours((v) => !v)}
            className="h-7 px-2 text-xs"
            data-testid="toggle-off-hours-btn"
            title={showOffHours ? 'Hide closed hours' : 'Show closed hours'}
          >
            {showOffHours ? <EyeOff className="h-3.5 w-3.5 mr-1" /> : <Eye className="h-3.5 w-3.5 mr-1" />}
            {showOffHours
              ? 'Hide off-hours'
              : hiddenCount > 0
                ? `Show off-hours (${hiddenCount})`
                : 'Show off-hours'}
          </Button>
          <div className="flex items-center gap-1 bg-blue-50 px-2 py-1 rounded">
            <Move className="h-3 w-3 text-blue-600" />
            <span className="text-blue-700">Drag bookings to reschedule</span>
          </div>
          <div className="flex items-center gap-1"><div className="w-3 h-3 rounded bg-green-100 border border-green-300" /><span>Available</span></div>
          <div className="flex items-center gap-1"><div className="w-3 h-3 rounded bg-red-100 border border-red-300" /><span>Coaching</span></div>
          <div className="flex items-center gap-1"><div className="w-3 h-3 rounded bg-sky-100 border border-sky-300" /><span>30 min</span></div>
          <div className="flex items-center gap-1"><div className="w-3 h-3 rounded bg-emerald-100 border border-emerald-300" /><span>1 hr</span></div>
          <div className="flex items-center gap-1"><div className="w-3 h-3 rounded bg-violet-100 border border-violet-300" /><span>1.5 hr</span></div>
          <div className="flex items-center gap-1"><div className="w-3 h-3 rounded bg-amber-100 border border-amber-300" /><span>2 hr</span></div>
          <div className="flex items-center gap-1"><div className="w-3 h-3 rounded bg-slate-200 border border-slate-300" /><span>Closed</span></div>
        </div>
      </div>
    </CardHeader>
    <CardContent className="p-0">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[700px]">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50">
              <th className="w-20 p-2 text-left text-xs font-medium text-slate-500 sticky left-0 bg-slate-50">Time</th>
              {lanes.map((lane) => (
                <th key={lane.id} className={`p-2 text-center text-sm font-medium ${LANE_COLORS[lane.id]?.text || DEFAULT_LANE_COLOR.text}`}>
                  {lane.name}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <CalendarRows
              visibleSlots={visibleSlots}
              lanes={lanes}
              draggedBooking={draggedBooking}
              isDragging={isDragging}
              dropTargetSlot={dropTargetSlot}
              getSlotStatus={getSlotStatus}
              getSlotSpanInfo={getSlotSpanInfo}
              onSlotClick={onSlotClick}
              onBookedSlotClick={onBookedSlotClick}
              onConflictClick={onConflictClick}
              onEditBlock={onEditBlock}
              onDeleteBlock={onDeleteBlock}
              onDragStart={onDragStart}
              onDragEnd={onDragEnd}
              onDragOver={onDragOver}
              onDragLeave={onDragLeave}
              onDrop={onDrop}
            />
          </tbody>
        </table>
      </div>
    </CardContent>
  </Card>
  );
};

export default CalendarGrid;
